import { CrewMerchantApi, CrewMerchantUi } from 'corso-types';
import { match } from 'ts-pattern';
import { z } from 'zod';
import { Description } from '~/components/DescriptionList';
import { formatter } from 'corso-web-lib';

type DiscountParameters = z.infer<
  typeof CrewMerchantUi.discountDefinitionSchema
>['parameters'];
type DiscountValue = DiscountParameters['customerGets']['value'];
type DiscountKind = DiscountParameters['kind'];
type MinimumRequirement = DiscountParameters['minimumRequirement'];
type DiscountDefinition = CrewMerchantApi.ResponseBody<
  '/:storeId/discount-definition',
  'get'
>[number];

const kindLabels = {
  amountOffOrder: 'Amount off order',
  amountOffProduct: 'Amount off products',
} as const satisfies Record<DiscountKind, string>;

export const discountKindLabel = (kind: DiscountKind) => kindLabels[kind];

export const formatDiscountValue = (
  value: DiscountValue,
  currencyCode: string,
) =>
  'percentage' in value
    ? `${value.percentage}% off`
    : `${formatter.currency(Number(value.amount), currencyCode)}${value.appliesOnEachItem ? ' off each item' : ' off'}`;

export const formatMinimumRequirement = (
  requirement: MinimumRequirement,
  currencyCode: string,
) =>
  match(requirement)
    .with({ kind: 'quantity' }, ({ quantity }) => {
      const count = quantity.greaterThanOrEqualToQuantity;
      return `${count} item${count === '1' ? '' : 's'} minimum`;
    })
    .with(
      { kind: 'subtotal' },
      ({ subtotal }) =>
        `${formatter.currency(Number(subtotal.greaterThanOrEqualToSubtotal), currencyCode)} minimum`,
    )
    .otherwise(() => 'No minimum');

export const formatCombinesWith = (
  combinesWith: DiscountParameters['combinesWith'],
) => {
  const parts: string[] = [];
  if (combinesWith.productDiscounts) parts.push('Product');
  if (combinesWith.orderDiscounts) parts.push('Order');
  if (combinesWith.shippingDiscounts) parts.push('Shipping');
  return parts.length === 0 ? 'None' : parts.join(', ');
};

const formatAppliesTo = (parameters: DiscountParameters) => {
  if (parameters.kind === 'amountOffOrder') return null;
  const count = parameters.customerGets.items.collections.add.length;
  return `${count} collection${count === 1 ? '' : 's'}`;
};

/** Builds the shared row set any caller renders via `<DescriptionList>`. */
export function buildDiscountDescriptions(
  definition: DiscountDefinition,
  currencyCode: string,
): Description[] {
  const { parameters } = definition.discountDefinition;

  const descriptions: Description[] = [
    { term: 'Type', details: discountKindLabel(parameters.kind) },
    {
      term: 'Value',
      details: formatDiscountValue(parameters.customerGets.value, currencyCode),
    },
  ];

  const appliesTo = formatAppliesTo(parameters);
  if (appliesTo) {
    descriptions.push({ term: 'Applies to', details: appliesTo });
  }

  descriptions.push({
    term: 'Minimum requirement',
    details: formatMinimumRequirement(
      parameters.minimumRequirement,
      currencyCode,
    ),
  });

  descriptions.push({
    term: 'Combines with',
    details: formatCombinesWith(parameters.combinesWith),
  });

  return descriptions;
}

/** Customer-facing one-liner — e.g. `$10 off` or `12% off`. */
export const buildDiscountMetaDescription = (
  definition: DiscountDefinition,
  currencyCode: string,
) =>
  formatDiscountValue(
    definition.discountDefinition.parameters.customerGets.value,
    currencyCode,
  );
